/* global globalThis */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ApiUrl, AppPages, Defines } from '../../../project/Defines';
import { EVENT_NAME } from '../../../project/utilities';
import ListView from '../../../Shared/ListView';
import { COLUMN_TYPE } from '../../../Shared/ListView/helper';
import { useTranslation } from 'react-i18next';

import moment from 'moment';

/**
 * Parse date from various formats, convert UTC to local timezone, and format to standard format.
 * @param {string} dateValue - Date string in various formats (assumed to be in UTC)
 * @param {{ withTime?: boolean }} options
 * @returns {string} - Formatted date string in YYYY/MM/DD (or YYYY/MM/DD HH:mm) format (local timezone)
 */
const parseAndFormatDate = (dateValue, options = {}) => {
  if (!dateValue) return dateValue;
  const { withTime = true } = options;
  
  // Try different date formats
  const formats = [
    'DD.MM.YYYY HH:mm:ss',  // 20.03.2026 11:12:00
    'DD.MM.YYYY HH:mm',     // 20.03.2026 11:12
    'MM/DD/YYYY HH:mm:ss',  // 03/12/2026 15:05:00
    'MM/DD/YYYY HH:mm',     // 03/12/2026 15:05
    'YYYY-MM-DD HH:mm:ss',  // ISO format with time
    'YYYY-MM-DDTHH:mm:ss',  // ISO format with T
    'YYYY-MM-DD HH:mm',     // ISO format without seconds
    'YYYY-MM-DD',           // ISO date only
  ];
  
  let parsedDate = null;
  
  // Try parsing with different formats
  for (const format of formats) {
    parsedDate = moment.utc(dateValue, format, true); // Parse as UTC in strict mode
    if (parsedDate.isValid()) {
      break;
    }
  }
  
  // If still not valid, try moment's default parsing as UTC (less strict)
  if (!parsedDate || !parsedDate.isValid()) {
    parsedDate = moment.utc(dateValue);
  }
  
  // If still invalid, return original value
  if (!parsedDate || !parsedDate.isValid()) {
    return dateValue;
  }
  
  // Convert UTC to local timezone and format
  return parsedDate
    .local()
    .format(withTime ? Defines.Format.MomentDateTime : Defines.Format.MomentDate);
};

/**
 * @exports History
 */
function History(props) {
  const { dataItem } = props;
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(globalThis.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(globalThis.innerWidth <= 768);
    };
    
    globalThis.addEventListener('resize', handleResize);
    return () => globalThis.removeEventListener('resize', handleResize);
  }, []);

  const columns = [
    {
      text: 'text.user',
      noSort: 'true',
      field: 'user_name',
      width: 300,
      type: COLUMN_TYPE.TEXT
    },
    {
      text: 'text.call_id',
      noSort: 'true',
      //field: 'call_id',
      render: (text, record) => {
        let call_id = "";
        if (record.change_data !== "") {
          call_id = JSON.parse(record.change_data).call_id;
        }
        return (
          <wrap><text>{call_id}</text></wrap>
        );
      },
      width: 300,
      type: COLUMN_TYPE.TEXT
    },
    {
      text: 'text.type',
      noSort: 'true',
      //field: 'what',
      width: 200,
      render: (text, record) => {
        let type = "";
        if (record.change_data !== "") {
          type = JSON.parse(record.change_data).type;
        }
        return (
          <wrap><text>{type}</text></wrap>
        );
      },
      type: COLUMN_TYPE.TEXT
    },
    {
      text: 'text.when',
      noSort: 'true',
      field: 'created_at',
      width: 300,
      render: (text, record) => {
        // debugger;
        let created_at = moment.utc(text).local().format(Defines.Format.MomentDateTime);
        return (
          <wrap><text>{created_at}</text></wrap>
        );
      },
      type: COLUMN_TYPE.TEXT
    }
  ];


  const expandedColumnConfig = [
    {
      text: 'text.field',
      noSort: 'true',
      width: 250,
      render: (text, record) => {
        let fieldName = record?.field;
        return (
          <wrap>
            <text>{t('text.' + fieldName)}</text>
          </wrap>
        );
      },
      type: COLUMN_TYPE.TEXT
    },
    {
      text: 'text.old_value',
      noSort: 'true',
      render: (text, record) => {
        let fieldName = record?.field;
        let old_value = record?.old_value;

        
        if(old_value) {
          if (fieldName === "accepted_at" || fieldName === "rejected_at" || fieldName === "appointment_date_at" ||
              fieldName === "start_date" || fieldName === "end_date" ||
              fieldName === "substatus_date" || fieldName === "opening_date"
          ) {
            old_value = parseAndFormatDate(old_value, { withTime: !(fieldName === "start_date" || fieldName === "end_date") });
          }
        }

        return (
          <wrap>
            <text>{old_value}</text>
          </wrap>
        );
      },
      width: 250,
      type: COLUMN_TYPE.TEXT
    },
    {
      text: 'text.new_value',
      noSort: 'true',
      render: (text, record) => {
        let fieldName = record?.field;
        let new_value = record?.new_value;

        if (new_value) {
          if (fieldName === "accepted_at" || fieldName === "rejected_at" || fieldName === "appointment_date_at" ||
              fieldName === "start_date" || fieldName === "end_date" ||
              fieldName === "substatus_date" || fieldName === "opening_date"
          ) {
            new_value = parseAndFormatDate(new_value, { withTime: !(fieldName === "start_date" || fieldName === "end_date") });
          }
        }
        
        return (
          <wrap>
            <text>{new_value}</text>
          </wrap>
        );
      },
      type: COLUMN_TYPE.TEXT
    }
  ];
 

  const onExpandedRowRender = useCallback((record) => {
    let dataSource = JSON.parse(record?.change_data)?.changedItem;
    
    // Ensure each item has a unique key for React
    if (dataSource && Array.isArray(dataSource)) {
      dataSource = dataSource.map((item, index) => ({
        ...item,
        key: item.key || item.ID || item.uuid || item.field || `expanded-row-${index}`
      }));
    }
    
    return record.change_data && <wrap><ListView
      isSimple
      isChieldView
      noPaging
      skipEdit
      skipNewButton={true}
      expandedTable={true}
      columnConfig={expandedColumnConfig}
      pagination={false}
      dataSource={dataSource}
      // showHeader={false}
      style={{ "height": "auto", "paddingBottom": "20px", "paddingLeft": "40px" }}

    /></wrap>;
  }, [expandedColumnConfig]);// eslint-disable-line react-hooks/exhaustive-deps


  return (
    <ListView
      {...props}
      className={`scroll_fix ${isMobile ? "visible_buttons" : ""}`}
      columnConfig={columns}
      apiUrl={ApiUrl.HistoryList}
      noSort
      noPaging
      skipNewButton
      noOverflow
      listUpdateEvent={EVENT_NAME.HISTORY_LIST_UPDATE}
      listName={AppPages.History}
      staticFilter={{ service_job_uuid: dataItem.ID }}
      expandable={{
        defaultExpandAllRows: false,
        expandedRowRender: record => onExpandedRowRender(record)
        // rowExpandable: (record) => {
        //     return JSON.parse(record.change_data).changedItem.length > 1;
        // },
      }}
    />
  );
}

History.propTypes = {
    dataItem: PropTypes.object.isRequired
};

export default connect(state => ({
  role: state.role,
  canCreateCall: state.userData.canCreateCall
}))(History);